import { Alert, Button, Field, Input } from '@personachat/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components } from '../../api/schema';
import { nouvelleCle } from '../../lib/fichiers';
import { formatCredits, formatNombre } from '../../lib/format';
import { LIBELLE_TYPE } from '../../lib/labels';

type Apercu = components['schemas']['BulkPreview'];

export interface Destinataires {
  userIds?: string[];
  emails?: string[];
}

/** E-mails collés (une par ligne, ou séparées par virgules, points-virgules, espaces). */
export function lireEmails(texte: string): string[] {
  return texte
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter(Boolean);
}

/**
 * Ajout groupé de crédits (cahier des charges §7.3) : montant et motif,
 * aperçu obligatoire (comptes trouvés, introuvables, exclus, plafonds), puis
 * validation. La clé d'opération ne change pas tant que la demande est la
 * même : relancer ne crédite pas deux fois.
 */
export function BulkCreditForm({
  destinataires,
  onTermine,
  onAnnuler,
}: {
  destinataires: Destinataires;
  onTermine: (credites: number) => void;
  onAnnuler?: () => void;
}) {
  const id = useId();
  const [montant, setMontant] = useState('');
  const [motif, setMotif] = useState('');
  const [apercu, setApercu] = useState<Apercu | null>(null);
  const [resultat, setResultat] = useState<{ credited: number; alreadyApplied: number; failed: Array<{ email: string; error: string }> } | null>(null);

  const cleDemande = JSON.stringify([destinataires.userIds ?? [], destinataires.emails ?? [], montant.trim(), motif.trim()]);
  const idempotencyKey = useMemo(() => nouvelleCle(), [cleDemande]);
  useEffect(() => {
    setApercu(null);
    setResultat(null);
  }, [cleDemande]);

  const nbDemandes = (destinataires.userIds?.length ?? 0) + (destinataires.emails?.length ?? 0);
  const amount = Number(montant);
  const montantValide = Number.isInteger(amount) && amount >= 1 && amount <= 10_000;
  const motifValide = motif.trim().length >= 3;

  const corps = (dryRun: boolean) => ({
    ...(destinataires.userIds?.length ? { userIds: destinataires.userIds } : {}),
    ...(destinataires.emails?.length ? { emails: destinataires.emails } : {}),
    amount,
    motif: motif.trim(),
    idempotencyKey,
    dryRun,
  });

  const voir = useMutation({
    mutationFn: () => lire(api.POST('/api/admin/credits/bulk', { body: corps(true) })),
    onSuccess: (d) => setApercu(d.preview),
  });

  const crediter = useMutation({
    mutationFn: () => lire(api.POST('/api/admin/credits/bulk', { body: corps(false) })),
    onSuccess: (d) => {
      setApercu(d.preview);
      if (d.result) {
        setResultat(d.result);
        onTermine(d.result.credited);
      }
    },
  });

  const demanderApercu = (e: FormEvent) => {
    e.preventDefault();
    if (montantValide && motifValide && nbDemandes > 0) voir.mutate();
  };

  const erreur = voir.error ?? crediter.error;

  return (
    <form className="stack" onSubmit={demanderApercu}>
      <div className="grid-2">
        <Field id={`${id}-montant`} label="Crédits par élève" hint="Entier, ajouté à chaque élève.">
          <Input
            id={`${id}-montant`}
            inputMode="numeric"
            value={montant}
            onChange={(e) => setMontant(e.target.value.replace(/[^\d]/g, ''))}
            placeholder="50"
            invalid={montant !== '' && !montantValide}
          />
        </Field>
        <Field id={`${id}-motif`} label="Motif" hint="Obligatoire, visible dans le journal des crédits.">
          <Input id={`${id}-motif`} value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={200} placeholder="Ex. : cohorte octobre, geste commercial" />
        </Field>
      </div>

      {!apercu && (
        <div className="row">
          {onAnnuler && <Button onClick={onAnnuler}>Annuler</Button>}
          <Button type="submit" variant="primary" loading={voir.isPending} disabled={!montantValide || !motifValide || nbDemandes === 0}>
            Voir l’aperçu
          </Button>
          {nbDemandes === 0 && <span className="note">Indiquez au moins une élève.</span>}
        </div>
      )}

      {erreur && <Alert>{messageDe(erreur)}</Alert>}

      {apercu && !resultat && (
        <ApercuAjout
          apercu={apercu}
          montant={amount}
          envoi={crediter.isPending}
          onModifier={() => setApercu(null)}
          onValider={() => crediter.mutate()}
        />
      )}

      {resultat && (
        <Alert tone="info">
          {formatNombre(resultat.credited)} élève{resultat.credited > 1 ? 's' : ''} créditée{resultat.credited > 1 ? 's' : ''} de {formatCredits(amount)}
          {resultat.alreadyApplied > 0 && ` ; ${formatNombre(resultat.alreadyApplied)} déjà créditée(s) par cette même opération, ignorée(s)`}
          {resultat.failed.length > 0 && ` ; échecs : ${resultat.failed.map((f) => `${f.email} (${f.error})`).join(', ')}`}.
        </Alert>
      )}
    </form>
  );
}

function ApercuAjout({
  apercu,
  montant,
  envoi,
  onModifier,
  onValider,
}: {
  apercu: Apercu;
  montant: number;
  envoi: boolean;
  onModifier: () => void;
  onValider: () => void;
}) {
  const a = apercu.allowance;
  return (
    <div className="stack apercu">
      <div className="confirm__impact">
        <strong>{formatNombre(apercu.toCredit)}</strong> élève{apercu.toCredit > 1 ? 's' : ''} à créditer de{' '}
        <strong>{formatCredits(montant)}</strong>, soit <strong>{formatCredits(apercu.total)}</strong> au total.
        {apercu.alreadyApplied > 0 && (
          <div className="note">{formatNombre(apercu.alreadyApplied)} déjà créditée(s) par cette opération : elles ne le seront pas deux fois.</div>
        )}
        {!a.illimite && (
          <div className="note">
            Votre plafond : {formatNombre(a.parOperation)} crédits par élève, {formatNombre(a.parJour)} par jour — encore{' '}
            {formatNombre(a.resteAujourdhui)} aujourd’hui.
          </div>
        )}
      </div>

      {apercu.refusal && <Alert>{apercu.refusal}</Alert>}

      {apercu.notFound.length + apercu.excluded.length + apercu.invalid.length > 0 && (
        <details className="apercu__details">
          <summary>
            Non créditées : {apercu.notFound.length} introuvable(s), {apercu.excluded.length} exclue(s), {apercu.invalid.length} adresse(s) invalide(s)
          </summary>
          <ul className="small">
            {apercu.notFound.map((e) => (
              <li key={`nf-${e}`}>{e} — introuvable</li>
            ))}
            {apercu.excluded.map((x) => (
              <li key={`ex-${x.email}`}>
                {x.email} — {x.raison}
              </li>
            ))}
            {apercu.invalid.map((e) => (
              <li key={`in-${e}`}>{e} — adresse invalide</li>
            ))}
          </ul>
        </details>
      )}

      {apercu.recipients.length > 0 && (
        <details className="apercu__details">
          <summary>Élèves trouvées ({formatNombre(apercu.recipients.length)})</summary>
          <ul className="small">
            {apercu.recipients.slice(0, 200).map((r) => (
              <li key={r.id}>
                {r.email} · {LIBELLE_TYPE[r.userType]} · solde {formatNombre(r.balance)}
                {r.alreadyApplied && ' · déjà créditée'}
              </li>
            ))}
            {apercu.recipients.length > 200 && <li>… et {formatNombre(apercu.recipients.length - 200)} autres</li>}
          </ul>
        </details>
      )}

      <div className="row">
        <Button onClick={onModifier} disabled={envoi}>
          Modifier
        </Button>
        <Button variant="primary" onClick={onValider} loading={envoi} disabled={!!apercu.refusal || apercu.toCredit === 0}>
          Créditer {formatNombre(apercu.toCredit)} élève{apercu.toCredit > 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );
}
